import {
  ChangeDetectorRef,
  Component,
  inject,
  Inject,
  SecurityContext,
  TemplateRef,
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { DomSanitizer } from "@angular/platform-browser";

import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { ActionButton } from "../buttons/gloss-button/actionButton";
import { isAllLowerCase, isCamelCase } from "@bitwarden/web-vault/app/shared/utils/helper-string";
import { NotificationType } from "@bitwarden/web-vault/app/models/types/confirmation.types";
import { StatusHeaderType } from "@bitwarden/web-vault/app/components/status-header/status-header.component";

@Component({
  selector: "app-confirmation-dialog",
  templateUrl: "./confirmation-dialog.component.html",
})
export class ConfirmationDialogComponent {
  private i18nService: I18nService = inject(I18nService);
  private sanitizer = inject(DomSanitizer);

  readonly isStandard: boolean;
  readonly statusHeaderType: StatusHeaderType | null;
  /** Final, human-readable title — either the translated key or an already-resolved `$GENERATED$` string. */
  readonly resolvedTitle: string;
  isMessageKey = false;
  safeHtmlMessage: string | null = null;
  /** True after confirm when using stay-open + loading flow before the post-confirm attachment action. */
  isReloading = false;
  closeBtnOptions: ActionButton;
  confirmBtnOptions: ActionButton;
  constructor(
    public dialogRef: MatDialogRef<ConfirmationDialogComponent>,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      title: string;
      /** True when `title` is an already-translated `$GENERATED$` string rather than an i18n key. */
      isTitleGenerated?: boolean;
      type?: NotificationType;
      message: string;
      singleButton?: boolean;
      stayOpenForPostConfirmAction?: boolean;
      stayOpenAfterConfirmAction?: () => void;
      dismissible?: boolean;
      templateContent: TemplateRef<any>;
      customizedButton: {
        yes: { text: string; class: string };
        no: { text: string; class: string };
      };
      /** Renders the confirm button in the destructive (red) style — see confirmation.constant.ts. */
      destructive?: boolean;
    },
  ) {
    // A status type is honoured even with templateContent: the template replaces only the body, and
    // the type still picks the glass surface panel class and buttons (confirmingBundleSwitch is
    // "info" + template + bottom sheet on purpose).
    this.statusHeaderType = data.type && data.type !== "success" ? data.type : null;
    // A "$GENERATED$" title (see ConfirmationDialogService) is already translated — i18n-ing it
    // again would look up a sentence as a key and render blank. Every other title is a key.
    this.resolvedTitle = data.isTitleGenerated ? data.title : this.i18nService.t(data.title);
    this.isStandard = !this.statusHeaderType;

    this.closeBtnOptions = new ActionButton({
      text: this.i18nService.t(this.data.customizedButton?.no.text ?? "no"),
      class: this.data.customizedButton?.no.class ?? "neutral",
      onClick: () => this.dialogRef.close(false), // todo @Marnie this is just to stop the console.errors
    });

    this.confirmBtnOptions = new ActionButton({
      text: this.data.singleButton
        ? this.i18nService.t(this.data.customizedButton?.yes?.text ?? "ok")
        : this.i18nService.t(this.data.customizedButton?.yes?.text ?? "yes"),
      class: this.data.customizedButton?.yes?.class ?? "primary",
      onClick: () => this.onConfirmClicked(),
      testId: "confirm-action-button",
    });

    if (data.message) {
      this.isMessageKey = isAllLowerCase(data.message) || isCamelCase(data.message);
      if (!this.isMessageKey) {
        this.safeHtmlMessage = this.sanitizer.sanitize(SecurityContext.HTML, data.message) ?? null;
      }
    }

    if (this.statusHeaderType) {
      // Matches the .confirmation-dialog--{type} classes in material-ui.scss — no separate map
      // to keep in sync with STATUS_HEADER_ICON_CONFIGS as StatusHeaderType grows.
      this.dialogRef.addPanelClass(`confirmation-dialog--${this.statusHeaderType}`);
    }
  }

  private onConfirmClicked(): void {
    if (this.data.stayOpenForPostConfirmAction) {
      this.isReloading = true;
      this.cdr.detectChanges();
      requestAnimationFrame(() => {
        this.data.stayOpenAfterConfirmAction?.();
      });
      return;
    }
    this.dialogRef.close(true);
  }
}
